/**
 * P&L Head dedicated login page.
 * Internally hits the same `/api/auth/admin-login` endpoint as the admin
 * console (which accepts role=pl_partner). Branded separately so the URL
 * can be safely shared with P&L Heads who shouldn't see the "Admin Console"
 * framing of the main /admin login. Routes pl_partner → /app/pl.
 */
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp, Mail, KeyRound, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CreceLogo } from "@/components/CreceLogo";

export default function PLLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin-login", { email, password });
      // Hard-stop if anyone other than a P&L Head somehow signs in here.
      // Admins are allowed too because they need to test the flow.
      const role = data.user?.role;
      if (!["pl_partner", "admin", "ceo"].includes(role)) {
        toast.error("This portal is for P&L Heads only.");
        return;
      }
      login(data.token, data.user);
      toast.success(role === "pl_partner" ? "Welcome back, P&L Head" : "Admin preview");
      navigate(role === "pl_partner" ? "/app/pl" : "/app/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-stone-900 p-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-25" style={{
        background: "radial-gradient(circle at 25% 25%, #064E3B 0%, transparent 50%), radial-gradient(circle at 75% 75%, #D97706 0%, transparent 55%)",
      }} />
      <div className="w-full max-w-md relative">
        <div className="flex flex-col items-center mb-8">
          <CreceLogo size="lg" theme="light" />
          <div className="mt-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
            <TrendingUp className="w-3.5 h-3.5" /> P&amp;L Head Portal
          </div>
        </div>
        <div className="bg-stone-50 rounded-2xl p-7 shadow-2xl shadow-emerald-900/30 backdrop-blur">
          <h1 className="font-display text-3xl font-black tracking-tight text-stone-900">Profit Share Login</h1>
          <p className="text-sm text-stone-600 mt-1.5">Use the email + password your admin issued you.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="pl-login-form">
            <div>
              <Label className="font-semibold text-xs uppercase tracking-wider text-stone-700">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input data-testid="pl-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 pl-10 rounded-lg" placeholder="you@example.com" />
              </div>
            </div>
            <div>
              <Label className="font-semibold text-xs uppercase tracking-wider text-stone-700">Password</Label>
              <div className="relative mt-1.5">
                <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input data-testid="pl-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pl-10 rounded-lg" placeholder="••••••••" />
              </div>
            </div>
            <Button type="submit" data-testid="pl-login-submit" disabled={loading}
              className="w-full bg-emerald-900 hover:bg-emerald-950 text-amber-300 font-bold rounded-full h-12">
              {loading ? "Signing in…" : <>Sign in <ArrowRight className="ml-1.5 w-4 h-4" /></>}
            </Button>
          </form>
          <Link to="/admin/forgot" className="block mt-4 text-xs text-center text-stone-500 hover:text-emerald-900">Forgot password?</Link>
        </div>
        <p className="text-center text-[11px] text-stone-400 mt-6">© Crece · P&amp;L Head Portal · By invitation only</p>
      </div>
    </div>
  );
}
